import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserSidebar } from "@/components/Sidebar";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { CreditCard, ArrowRight, TrendingUp, CheckCircle, Clock, Wallet, Send, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/utils";

const StatusBadge = ({ s }) => {
  const map = { PAID: "bg-green-500/20 text-green-400 border-green-500/30", PENDING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", FAILED: "bg-red-500/20 text-red-400 border-red-500/30", REFUNDED: "bg-gray-500/20 text-gray-300 border-gray-500/30", CANCELLED: "bg-gray-500/20 text-gray-400 border-gray-500/30" };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${map[s] || ""}`} data-testid={`status-${s}`}>{s}</span>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ total_paid: 0, payment_count: 0, recent_payments: [], active_requests: [], wallet_balance: 0, wallet_currency: "EUR" });
  const [busy, setBusy] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/users/me/summary"); setSummary(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const pay = async (pr) => {
    setBusy(pr.id);
    try {
      const { data } = await api.post("/payments/from-request", { payment_request_id: pr.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setBusy(null); }
  };

  const delPayment = async () => {
    try { await api.delete(`/payments/${confirmDel.id}`); toast.success("Uplata obrisana"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen">
      <UserSidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-10 stream-scroll">
        <div className="max-w-6xl">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6 sm:mb-8">
            <div className="flex items-center gap-4 sm:gap-5">
              <Avatar user={user} size={64} className="sm:!w-20 sm:!h-20" />
              <div>
                <p className="text-white/60 text-xs sm:text-sm">Dobrodošli natrag</p>
                <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white leading-tight" data-testid="hello-user">{user?.first_name} {user?.last_name}</h1>
                <p className="text-white/50 text-xs sm:text-sm mt-1 break-all">{user?.email}</p>
              </div>
            </div>
            <Link to="/payments/new" className="w-full sm:w-auto"><Button className="btn-yellow rounded-full px-6 h-11 w-full sm:w-auto" data-testid="new-payment-cta"><CreditCard size={16} className="mr-2"/>Nova uplata</Button></Link>
          </div>

          {/* Wallet Balance Card */}
          <Link to="/wallet" className="block mb-6 sm:mb-8" data-testid="wallet-card-link">
            <div className="balance-card relative overflow-hidden rounded-3xl group transition-transform hover:scale-[1.01]" style={{
              background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 45%, #FFD60A22 100%)",
              border: "1px solid rgba(255,214,10,0.28)"
            }}>
              <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-[#FFD60A] blur-[100px] opacity-20"></div>
              <div className="relative p-5 sm:p-8 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#FFD60A] flex items-center justify-center shrink-0">
                    <Wallet size={22} className="text-black" />
                  </div>
                  <div>
                    <div className="text-white/50 text-xs uppercase tracking-widest">Saldo novčanika</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <div className="font-heading font-black text-3xl sm:text-4xl text-[#FFD60A]" data-testid="dashboard-wallet-balance">
                        {formatMoney((summary.wallet_balance || 0))}
                      </div>
                      <div className="text-white/60 text-lg">{summary.wallet_currency || "EUR"}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[#FFD60A] text-sm font-semibold">
                  <Send size={14} /> Zatraži isplatu <ArrowRight size={14} />
                </div>
              </div>
            </div>
          </Link>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
            <StatCard icon={CheckCircle} label="Status" value={user?.status || "ACTIVE"} accent="text-green-400" />
            <StatCard icon={Wallet} label="Ukupno plaćeno" value={`${formatMoney(summary.total_paid)} €`} accent="text-[#FFD60A]" />
            <StatCard icon={TrendingUp} label="Broj uplata" value={summary.payment_count} accent="text-white" />
            <StatCard icon={Clock} label="Aktivnih zahtjeva" value={summary.active_requests.length} accent="text-white" />
          </div>

          {/* Active requests */}
          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-white mb-4">Aktivni zahtjevi za uplatu</h2>
            {summary.active_requests.length === 0 ? (
              <div className="card-dark p-8 text-center text-white/50" data-testid="no-requests">Trenutno nema aktivnih zahtjeva.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {summary.active_requests.map((r) => (
                  <div key={r.id} className="card-dark p-6" data-testid={`request-${r.id}`}>
                    <div className="text-xs uppercase tracking-widest text-[#FFD60A] mb-2">Zahtjev za uplatu</div>
                    <div className="font-heading font-black text-3xl text-white">{formatMoney(r.amount)} {r.currency}</div>
                    <p className="text-white/60 mt-2 text-sm">{r.description}</p>
                    <Button onClick={() => pay(r)} disabled={busy === r.id} className="btn-yellow rounded-full mt-6 h-11 px-6" data-testid={`pay-${r.id}`}>
                      {busy === r.id ? "Prosljeđujemo…" : "PLATI PUTEM STRIPE"} <ArrowRight size={16} className="ml-2"/>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent payments */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-2xl text-white">Zadnje uplate</h2>
              <Link to="/payments" className="text-[#FFD60A] text-sm">Vidi sve →</Link>
            </div>
            <div className="card-dark overflow-hidden">
              {summary.recent_payments.length === 0 ? (
                <div className="p-8 text-center text-white/50">Još nema uplata.</div>
              ) : (
                <table className="w-full">
                  <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
                    <tr><th className="p-4">Datum</th><th className="p-4">Iznos</th><th className="p-4 hidden md:table-cell">Opis</th><th className="p-4">Status</th><th className="p-4 text-right">Akcija</th></tr>
                  </thead>
                  <tbody>
                    {summary.recent_payments.map((p) => (
                      <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4 text-white/70 text-sm">{new Date(p.created_at).toLocaleString("hr-HR")}</td>
                        <td className="p-4 text-white font-semibold">{formatMoney(p.amount)} {p.currency}</td>
                        <td className="p-4 text-white/60 hidden md:table-cell">{p.description}</td>
                        <td className="p-4"><StatusBadge s={p.status} /></td>
                        <td className="p-4 text-right">
                          <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => setConfirmDel(p)} data-testid={`del-recent-pay-${p.id}`}><Trash2 size={14}/></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </main>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-[#171717] border-white/10 text-white">
          <DialogHeader><DialogTitle>Obrisati uplatu?</DialogTitle></DialogHeader>
          <p className="text-white/60 text-sm">
            {confirmDel && <>Uplata {formatMoney(confirmDel.amount)} {confirmDel.currency} će biti trajno uklonjena iz vaše povijesti.</>}
          </p>
          <DialogFooter>
            <Button variant="ghost" className="text-white/70" onClick={() => setConfirmDel(null)} data-testid="del-recent-cancel">Odustani</Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white rounded-full" onClick={delPayment} data-testid="del-recent-confirm">Obriši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-dark p-4 sm:p-6" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/40">{label}</div>
        <Icon size={16} className={accent} />
      </div>
      <div className={`mt-2 sm:mt-3 font-heading font-black text-lg sm:text-2xl ${accent}`}>{value}</div>
    </div>
  );
}
